import ClearIcon from "@mui/icons-material/Clear";
import CopyAllIcon from "@mui/icons-material/CopyAll";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { ReactNode, useMemo, useState } from "react";
import { useFragment } from "react-relay";
import { useNavigate } from "react-router-dom";
import {
  AppBuildState,
  InternHome_query$data,
  InternHome_query$key,
  SalesChannelAttributionMethod,
} from "./__generated__/InternHome_query.graphql";
import useDuplicateBrandMutation from "../../mutations/useDuplicateBrandMutation";
import useSyncAppleDeveloperAgreementsForAllBrandsMutation from "../../mutations/useSyncAppleDeveloperAgreementsForAllBrandsMutation";
import InternDuplicateBrandDialog from "./brands/InternDuplicateBrandDialog";
import InternShopifyTokenDialog from "./brands/InternShopifyTokenDialog";
import InternStatusBadge from "./components/InternStatusBadge";

const queryFragment = graphql`
  fragment InternHome_query on Query {
    user {
      isSuperuser
    }
    internBrands {
      id
      isBrandAdmin
      displayName
      facebookConfig {
        id
        needsReauth
      }
      appleDeveloperAgreementsPendingSince
      showIosTosAlert
      tempoApiToken
      iosPushNotificationCredentialsRequireServiceKeyMigration
      androidPushNotificationRequiresFcmv1Migration
      shopifyConfig {
        id
        multipassEnabled
        tokenState
      }
      appConfig {
        appState
        salesChannelAttributionMethod
      }
    }
  }
`;

type SortField = "displayName" | "appState" | "salesChannel";
type SortDirection = "asc" | "desc";
type FlagFilter = "pending_agreements" | "meta_reauth" | "shopify_token";
type IntegrationFilter =
  | "tempo"
  | "admin"
  | "multipass"
  | "ios_migration"
  | "fcmv1";

const APP_STATE_ORDER: AppBuildState[] = [
  "PRODUCTION",
  "PRE_PRODUCTION",
  "PENDING_KINN_SETUP",
  "PENDING_BRAND_SETUP",
  "DISABLED",
];

const APP_STATE_CONFIG: Record<
  AppBuildState,
  {
    label: string;
    status: "success" | "warning" | "error" | "info" | "default";
  }
> = {
  PRODUCTION: { label: "Production", status: "success" },
  PRE_PRODUCTION: { label: "Pre-Production", status: "info" },
  PENDING_KINN_SETUP: { label: "Pending Kinn", status: "error" },
  PENDING_BRAND_SETUP: { label: "Pending Brand", status: "warning" },
  DISABLED: { label: "Disabled", status: "default" },
  "%future added value": { label: "Unknown", status: "default" },
};

const SALES_CHANNEL_ORDER: Array<SalesChannelAttributionMethod | null> = [
  "NATIVE_CART",
  "SERIALIZED_GRAPHQL",
  null,
];

const SALES_CHANNEL_CONFIG: Record<
  SalesChannelAttributionMethod | "%future added value",
  { label: string; shortLabel: string }
> = {
  NATIVE_CART: { label: "Native Cart", shortLabel: "Native" },
  SERIALIZED_GRAPHQL: {
    label: "Serialized GraphQL",
    shortLabel: "Serialized",
  },
  "%future added value": { label: "Unknown", shortLabel: "Unknown" },
};

const FLAG_FILTERS: {
  value: FlagFilter;
  label: string;
  status: "error" | "warning";
}[] = [
  { value: "pending_agreements", label: "Pending Agreements", status: "error" },
  { value: "meta_reauth", label: "Meta Re-auth", status: "warning" },
  { value: "shopify_token", label: "No Shopify Token", status: "error" },
];

const INTEGRATION_FILTERS: { value: IntegrationFilter; label: string }[] = [
  { value: "tempo", label: "Tempo" },
  { value: "admin", label: "Admin" },
  { value: "multipass", label: "Multipass" },
  { value: "ios_migration", label: "iOS Migration" },
  { value: "fcmv1", label: "FCMv1" },
];

const DEFAULT_STATES: AppBuildState[] = ["PRE_PRODUCTION", "PRODUCTION"];

const chipSx = {
  height: 20,
  fontSize: "0.6875rem",
  fontWeight: 500,
} as const;

const filterChipSx = {
  cursor: "pointer",
  height: 26,
  fontSize: "0.75rem",
} as const;

type BrandRow = InternHome_query$data["internBrands"][number];

const getBrandFlags = (brand: BrandRow) => ({
  pendingAgreements: brand.appleDeveloperAgreementsPendingSince != null,
  metaReauth: brand.facebookConfig?.needsReauth ?? false,
  shopifyTokenMissing:
    (brand.shopifyConfig?.tokenState ?? "disconnected") === "disconnected",
});

const getBrandIntegrations = (brand: BrandRow) => ({
  tempo: Boolean(brand.tempoApiToken),
  admin: brand.isBrandAdmin,
  multipass: brand.shopifyConfig?.multipassEnabled ?? false,
  iosMigration: brand.iosPushNotificationCredentialsRequireServiceKeyMigration,
  fcmv1: brand.androidPushNotificationRequiresFcmv1Migration,
});

const toggleValue = <T,>(values: T[], value: T): T[] =>
  values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];

const InternHome = ({ query: queryKey }: { query: InternHome_query$key }) => {
  const query = useFragment(queryFragment, queryKey);
  const brands = useMemo(() => query.internBrands ?? [], [query.internBrands]);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStates, setSelectedStates] =
    useState<AppBuildState[]>(DEFAULT_STATES);
  const [selectedFlags, setSelectedFlags] = useState<FlagFilter[]>([]);
  const [selectedIntegrations, setSelectedIntegrations] = useState<
    IntegrationFilter[]
  >([]);
  const [showFilters, setShowFilters] = useState(true);

  const [sortField, setSortField] = useState<SortField>("displayName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const [syncAgreementsMutation, isSyncInFlight] =
    useSyncAppleDeveloperAgreementsForAllBrandsMutation();
  const [duplicateBrandMutation, isDuplicateInFlight] =
    useDuplicateBrandMutation();
  const [duplicateSourceBrand, setDuplicateSourceBrand] = useState<{
    id: string;
    displayName: string;
  } | null>(null);
  const [tokenBrand, setTokenBrand] = useState<{
    id: string;
    displayName: string;
  } | null>(null);

  const hasNonDefaultFilters =
    searchQuery.length > 0 ||
    selectedFlags.length > 0 ||
    selectedIntegrations.length > 0 ||
    selectedStates.length !== DEFAULT_STATES.length ||
    !DEFAULT_STATES.every((state) => selectedStates.includes(state));

  const activeFilterCount =
    (selectedStates.length > 0 ? 1 : 0) +
    (selectedFlags.length > 0 ? 1 : 0) +
    (selectedIntegrations.length > 0 ? 1 : 0) +
    (searchQuery ? 1 : 0);

  const filteredBrands = useMemo(() => {
    let result = brands.filter((brand) => {
      // No selected statuses, or every known status selected = no status
      // filter (show all brands, including unrecognized app states).
      const matchesStatus =
        selectedStates.length === 0 ||
        selectedStates.length === APP_STATE_ORDER.length ||
        selectedStates.includes(brand.appConfig.appState);
      if (!matchesStatus) {
        return false;
      }

      if (searchQuery) {
        const queryText = searchQuery.toLowerCase();
        if (!brand.displayName.toLowerCase().includes(queryText)) {
          return false;
        }
      }

      if (selectedFlags.length > 0) {
        const flags = getBrandFlags(brand);
        const matchesFlag = selectedFlags.some((flag) => {
          if (flag === "pending_agreements") return flags.pendingAgreements;
          if (flag === "meta_reauth") return flags.metaReauth;
          if (flag === "shopify_token") return flags.shopifyTokenMissing;
          return false;
        });
        if (!matchesFlag) return false;
      }

      if (selectedIntegrations.length > 0) {
        const integrations = getBrandIntegrations(brand);
        const matchesIntegration = selectedIntegrations.some((integration) => {
          if (integration === "tempo") return integrations.tempo;
          if (integration === "admin") return integrations.admin;
          if (integration === "multipass") return integrations.multipass;
          if (integration === "ios_migration") return integrations.iosMigration;
          if (integration === "fcmv1") return integrations.fcmv1;
          return false;
        });
        if (!matchesIntegration) return false;
      }

      return true;
    });

    result = [...result].sort((a, b) => {
      let comparison = 0;

      if (sortField === "displayName") {
        comparison = a.displayName.localeCompare(b.displayName);
      } else if (sortField === "appState") {
        comparison =
          APP_STATE_ORDER.indexOf(a.appConfig.appState) -
          APP_STATE_ORDER.indexOf(b.appConfig.appState);
      } else if (sortField === "salesChannel") {
        comparison =
          SALES_CHANNEL_ORDER.indexOf(
            a.appConfig.salesChannelAttributionMethod
          ) -
          SALES_CHANNEL_ORDER.indexOf(
            b.appConfig.salesChannelAttributionMethod
          );
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [
    brands,
    searchQuery,
    selectedStates,
    selectedFlags,
    selectedIntegrations,
    sortField,
    sortDirection,
  ]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedStates(DEFAULT_STATES);
    setSelectedFlags([]);
    setSelectedIntegrations([]);
  };

  const handleRowClick = (brandId: string) => {
    navigate(`/intern/brands/${brandId}/overview`);
  };

  const handleDuplicateBrand = (data: {
    sourceBrandId: string;
    displayName: string;
    iosAppIdentifier?: string | null;
    androidAppIdentifier?: string | null;
  }) => {
    duplicateBrandMutation(data, (response) => {
      const newBrand = response.duplicateBrand?.brand;
      if (!newBrand) {
        return;
      }

      setDuplicateSourceBrand(null);
      navigate(`/intern/brands/${newBrand.id}/overview`);
    });
  };

  const columnCount = isMobile ? 4 : 6;

  return (
    <Box sx={{ maxWidth: "100%", overflow: "hidden" }}>
      <InternDuplicateBrandDialog
        sourceBrand={duplicateSourceBrand}
        isInFlight={isDuplicateInFlight}
        onClose={() => setDuplicateSourceBrand(null)}
        onSubmit={handleDuplicateBrand}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={1}
      >
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
          >
            Brands
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "var(--intern-text-secondary)",
              fontWeight: 500,
            }}
          >
            {filteredBrands.length}
            <Box
              component="span"
              sx={{ color: "var(--intern-text-tertiary)", fontWeight: 400 }}
            >
              {" "}
              of {brands.length}
            </Box>
          </Typography>
        </Stack>
        <Tooltip title="Sync Apple Developer Agreements">
          <IconButton
            onClick={() => syncAgreementsMutation()}
            disabled={isSyncInFlight}
            size="small"
            sx={{
              color: "var(--intern-text-secondary)",
              border: "1px solid var(--intern-border-default)",
              borderRadius: 1.5,
              "&:hover": { bgcolor: "var(--intern-bg-hover)" },
            }}
          >
            <SyncIcon
              fontSize="small"
              sx={{
                animation: isSyncInFlight ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Tooltip>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 1.5, sm: 2 },
          bgcolor: "var(--intern-bg-primary)",
          border: "1px solid var(--intern-border-default)",
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.75}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              placeholder="Search brands..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                maxWidth: { xs: "100%", sm: 360 },
                "& .MuiOutlinedInput-root": {
                  bgcolor: "var(--intern-bg-secondary)",
                  borderRadius: 1.5,
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                  "& fieldset": {
                    borderColor: "var(--intern-border-default)",
                  },
                  "&:hover fieldset": {
                    borderColor:
                      "var(--intern-border-strong, var(--intern-border-default))",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{
                        color: "var(--intern-text-tertiary)",
                        fontSize: { xs: 18, sm: 20 },
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery("")}
                      edge="end"
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <Button
                size="small"
                variant={showFilters ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: 1.5,
                  minWidth: "auto",
                  px: 1.5,
                  ...(showFilters
                    ? {
                        bgcolor: "var(--intern-accent)",
                        color: "#fff",
                        "&:hover": { bgcolor: "var(--intern-accent)" },
                      }
                    : {
                        borderColor: "var(--intern-border-default)",
                        color: "var(--intern-text-secondary)",
                      }),
                }}
              >
                Filters
                {activeFilterCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75,
                      px: 0.75,
                      py: 0.1,
                      borderRadius: 10,
                      bgcolor: showFilters
                        ? "rgba(255,255,255,0.25)"
                        : "var(--intern-accent-bg)",
                      color: showFilters ? "#fff" : "var(--intern-accent)",
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {activeFilterCount}
                  </Box>
                )}
              </Button>
            </Tooltip>
            {hasNonDefaultFilters && (
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  textTransform: "none",
                  color: "var(--intern-text-secondary)",
                  fontWeight: 500,
                  display: { xs: "none", sm: "inline-flex" },
                }}
              >
                Clear
              </Button>
            )}
          </Stack>

          {showFilters && (
            <>
              <Divider sx={{ borderColor: "var(--intern-border-default)" }} />
              <Stack spacing={1.5}>
                <FilterGroup label="Status">
                  {APP_STATE_ORDER.map((state) => {
                    const config = APP_STATE_CONFIG[state];
                    const isSelected = selectedStates.includes(state);
                    return (
                      <Chip
                        key={state}
                        label={config.label}
                        size="small"
                        variant={isSelected ? "filled" : "outlined"}
                        onClick={() =>
                          setSelectedStates((prev) => {
                            const next = toggleValue(prev, state);
                            // Keep chips in sync with "no status filter":
                            // deselecting the last chip selects every status.
                            return next.length === 0
                              ? [...APP_STATE_ORDER]
                              : next;
                          })
                        }
                        sx={{
                          ...filterChipSx,
                          ...(isSelected
                            ? {
                                bgcolor:
                                  config.status === "default"
                                    ? "var(--intern-bg-secondary)"
                                    : `var(--intern-status-${config.status}-bg)`,
                                color:
                                  config.status === "default"
                                    ? "var(--intern-text-secondary)"
                                    : `var(--intern-status-${config.status})`,
                                border: "1px solid transparent",
                                fontWeight: 600,
                              }
                            : {
                                borderColor: "var(--intern-border-default)",
                                color: "var(--intern-text-secondary)",
                              }),
                        }}
                      />
                    );
                  })}
                </FilterGroup>

                <FilterGroup label="Flags">
                  {FLAG_FILTERS.map((filter) => {
                    const isSelected = selectedFlags.includes(filter.value);
                    return (
                      <Chip
                        key={filter.value}
                        label={filter.label}
                        size="small"
                        variant={isSelected ? "filled" : "outlined"}
                        onClick={() =>
                          setSelectedFlags((prev) =>
                            toggleValue(prev, filter.value)
                          )
                        }
                        sx={{
                          ...filterChipSx,
                          ...(isSelected
                            ? {
                                bgcolor: `var(--intern-status-${filter.status}-bg)`,
                                color: `var(--intern-status-${filter.status})`,
                                border: "1px solid transparent",
                                fontWeight: 600,
                              }
                            : {
                                borderColor: "var(--intern-border-default)",
                                color: "var(--intern-text-secondary)",
                              }),
                        }}
                      />
                    );
                  })}
                </FilterGroup>

                <FilterGroup label="Integrations">
                  {INTEGRATION_FILTERS.map((filter) => {
                    const isSelected = selectedIntegrations.includes(
                      filter.value
                    );
                    return (
                      <Chip
                        key={filter.value}
                        label={filter.label}
                        size="small"
                        variant={isSelected ? "filled" : "outlined"}
                        onClick={() =>
                          setSelectedIntegrations((prev) =>
                            toggleValue(prev, filter.value)
                          )
                        }
                        sx={{
                          ...filterChipSx,
                          ...(isSelected
                            ? {
                                bgcolor: "var(--intern-accent-bg)",
                                color: "var(--intern-accent)",
                                border: "1px solid transparent",
                                fontWeight: 600,
                              }
                            : {
                                borderColor: "var(--intern-border-default)",
                                color: "var(--intern-text-secondary)",
                              }),
                        }}
                      />
                    );
                  })}
                </FilterGroup>
              </Stack>
            </>
          )}
        </Stack>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: "var(--intern-bg-primary)",
          border: "1px solid var(--intern-border-default)",
          borderRadius: 2,
          maxWidth: "100%",
          overflowX: "auto",
          "& .MuiTableCell-root": {
            borderColor: "var(--intern-border-default)",
            px: { xs: 1, sm: 1.5 },
            py: { xs: 0.75, sm: 1 },
          },
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: { xs: 420, sm: 720 } }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: "var(--intern-bg-secondary)",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: "var(--intern-text-secondary)",
                }}
              >
                <TableSortLabel
                  active={sortField === "displayName"}
                  direction={
                    sortField === "displayName" ? sortDirection : "asc"
                  }
                  onClick={() => handleSort("displayName")}
                >
                  Brand
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  bgcolor: "var(--intern-bg-secondary)",
                  width: { xs: 90, sm: 130 },
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: "var(--intern-text-secondary)",
                }}
              >
                <TableSortLabel
                  active={sortField === "appState"}
                  direction={sortField === "appState" ? sortDirection : "asc"}
                  onClick={() => handleSort("appState")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <TableCell
                  sx={{
                    bgcolor: "var(--intern-bg-secondary)",
                    width: 150,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "var(--intern-text-secondary)",
                  }}
                >
                  <TableSortLabel
                    active={sortField === "salesChannel"}
                    direction={
                      sortField === "salesChannel" ? sortDirection : "asc"
                    }
                    onClick={() => handleSort("salesChannel")}
                  >
                    Sales Channel
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell
                sx={{
                  bgcolor: "var(--intern-bg-secondary)",
                  width: { xs: 100, sm: 170 },
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: "var(--intern-text-secondary)",
                }}
              >
                Flags
              </TableCell>
              {!isMobile && (
                <TableCell
                  sx={{
                    bgcolor: "var(--intern-bg-secondary)",
                    width: 220,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    color: "var(--intern-text-secondary)",
                  }}
                >
                  Integrations
                </TableCell>
              )}
              <TableCell
                sx={{
                  bgcolor: "var(--intern-bg-secondary)",
                  width: { xs: 72, sm: 88 },
                }}
                align="right"
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredBrands.map((brand) => {
              const stateConfig = APP_STATE_CONFIG[brand.appConfig.appState];
              const flags = getBrandFlags(brand);
              const integrations = getBrandIntegrations(brand);
              const salesChannel =
                brand.appConfig.salesChannelAttributionMethod;
              const salesChannelLabel = salesChannel
                ? isMobile
                  ? SALES_CHANNEL_CONFIG[salesChannel].shortLabel
                  : SALES_CHANNEL_CONFIG[salesChannel].label
                : null;

              return (
                <TableRow
                  key={brand.id}
                  onClick={() => handleRowClick(brand.id)}
                  sx={{
                    cursor: "pointer",
                    transition: "background-color 0.12s ease",
                    "&:hover": {
                      bgcolor: "var(--intern-bg-hover)",
                    },
                    "&:last-child td": {
                      borderBottom: 0,
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        color: "var(--intern-text-primary)",
                        wordBreak: "break-word",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                      }}
                    >
                      {brand.displayName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <InternStatusBadge
                      status={stateConfig.status}
                      label={
                        isMobile
                          ? stateConfig.label.split(" ")[0]
                          : stateConfig.label
                      }
                      size="small"
                      variant="subtle"
                    />
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      {salesChannelLabel ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "var(--intern-text-secondary)",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                          }}
                        >
                          {salesChannelLabel}
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "var(--intern-text-tertiary)",
                            fontSize: "0.75rem",
                          }}
                        >
                          —
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {flags.pendingAgreements && (
                        <Chip
                          label={isMobile ? "Pending" : "Pending Agreements"}
                          size="small"
                          color="error"
                          sx={{ height: 18, fontSize: "0.625rem" }}
                        />
                      )}
                      {flags.metaReauth && (
                        <Chip
                          label={isMobile ? "Meta" : "Meta Re-auth"}
                          size="small"
                          color="warning"
                          sx={{ height: 18, fontSize: "0.625rem" }}
                        />
                      )}
                      {flags.shopifyTokenMissing && (
                        <Tooltip title="Shopify admin API token was cleared by an uninstall or a failed refresh">
                          <Chip
                            label={isMobile ? "No Token" : "No Shopify Token"}
                            size="small"
                            color="error"
                            sx={{ height: 18, fontSize: "0.625rem" }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {integrations.tempo && (
                          <Chip
                            label="Tempo"
                            size="small"
                            variant="outlined"
                            sx={{
                              ...chipSx,
                              borderColor: "var(--intern-status-success)",
                              color: "var(--intern-status-success)",
                            }}
                          />
                        )}
                        {integrations.admin && (
                          <Chip
                            label="Admin"
                            size="small"
                            variant="outlined"
                            sx={{
                              ...chipSx,
                              borderColor: "var(--intern-status-info)",
                              color: "var(--intern-status-info)",
                            }}
                          />
                        )}
                        {integrations.multipass && (
                          <Chip
                            label="Multipass"
                            size="small"
                            variant="outlined"
                            sx={{
                              ...chipSx,
                              borderColor: "var(--intern-status-success)",
                              color: "var(--intern-status-success)",
                            }}
                          />
                        )}
                        {integrations.iosMigration && (
                          <Tooltip title="iOS push notifications need service key migration">
                            <Chip
                              icon={
                                <WarningAmberIcon
                                  sx={{ fontSize: "0.75rem !important" }}
                                />
                              }
                              label="iOS Migration"
                              size="small"
                              variant="outlined"
                              sx={{
                                ...chipSx,
                                borderColor: "#f59e0b",
                                color: "#f59e0b",
                                "& .MuiChip-icon": {
                                  color: "#f59e0b",
                                },
                              }}
                            />
                          </Tooltip>
                        )}
                        {integrations.fcmv1 && (
                          <Tooltip title="Android push notifications need FCMv1 migration">
                            <Chip
                              icon={
                                <WarningAmberIcon
                                  sx={{ fontSize: "0.75rem !important" }}
                                />
                              }
                              label="FCMv1"
                              size="small"
                              variant="outlined"
                              sx={{
                                ...chipSx,
                                borderColor: "#f59e0b",
                                color: "#f59e0b",
                                "& .MuiChip-icon": {
                                  color: "#f59e0b",
                                },
                              }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      justifyContent="flex-end"
                      spacing={0.25}
                    >
                      <Tooltip title="Shopify token">
                        <IconButton
                          size="small"
                          sx={{
                            color: "var(--intern-text-tertiary)",
                            "&:hover": {
                              color: "var(--intern-text-primary)",
                              bgcolor: "var(--intern-bg-secondary)",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTokenBrand({
                              id: brand.id,
                              displayName: brand.displayName,
                            });
                          }}
                        >
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton
                          size="small"
                          sx={{
                            color: "var(--intern-text-tertiary)",
                            "&:hover": {
                              color: "var(--intern-text-primary)",
                              bgcolor: "var(--intern-bg-secondary)",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDuplicateSourceBrand({
                              id: brand.id,
                              displayName: brand.displayName,
                            });
                          }}
                        >
                          <CopyAllIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open">
                        <IconButton
                          size="small"
                          sx={{
                            color: "var(--intern-text-tertiary)",
                            "&:hover": {
                              color: "var(--intern-text-primary)",
                              bgcolor: "var(--intern-bg-secondary)",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(brand.id);
                          }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredBrands.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columnCount}
                  sx={{ py: 8, textAlign: "center" }}
                >
                  <Stack spacing={1} alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{
                        color: "var(--intern-text-secondary)",
                        fontWeight: 500,
                      }}
                    >
                      No brands match your filters
                    </Typography>
                    {hasNonDefaultFilters && (
                      <Button
                        size="small"
                        onClick={handleClearFilters}
                        sx={{ textTransform: "none" }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {tokenBrand && (
        <InternShopifyTokenDialog
          brandId={tokenBrand.id}
          displayName={tokenBrand.displayName}
          isSuperuser={query.user?.isSuperuser ?? false}
          onClose={() => setTokenBrand(null)}
        />
      )}

      {filteredBrands.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 1.25,
            px: 0.5,
            color: "var(--intern-text-tertiary)",
          }}
        >
          Showing {filteredBrands.length} of {brands.length} brands
        </Typography>
      )}
    </Box>
  );
};

const FilterGroup = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={{ xs: 0.75, sm: 1.25 }}
    alignItems={{ xs: "flex-start", sm: "center" }}
  >
    <Typography
      variant="caption"
      sx={{
        color: "var(--intern-text-tertiary)",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        minWidth: 88,
        fontSize: "0.6875rem",
      }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {children}
    </Stack>
  </Stack>
);

export default InternHome;
